import React, { useRef, useState, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import SuccessModal from './SuccessModal';
import { RECAPTCHA_SITE_KEY, EMAILJS_CONFIG } from '../config';

const ContactForm = () => {
    const form = useRef();
    const recaptchaRef = useRef();
    const [status, setStatus] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);

    const handleCaptchaChange = useCallback((token) => {
        setCaptchaToken(token);
    }, []);

    const handleCaptchaExpired = useCallback(() => {
        setCaptchaToken(null);
    }, []);

    const sendEmail = (e) => {
        e.preventDefault();

        // Check reCAPTCHA
        if (!captchaToken) {
            alert('Please complete the reCAPTCHA verification.');
            return;
        }

        setStatus('sending');

        emailjs.sendForm(
            EMAILJS_CONFIG.SERVICE_ID,
            EMAILJS_CONFIG.TEMPLATES.CONTACT,
            form.current,
            EMAILJS_CONFIG.PUBLIC_KEY
        )
            .then((result) => {
                console.log(result.text);
                setStatus('success');
                form.current.reset();
                setCaptchaToken(null);
                recaptchaRef.current?.reset();
            }, (error) => {
                console.log(error.text);
                setStatus('error');
            });
    };

    // Updated input style to match the clean outline look from the design
    const inputStyle = "w-full bg-white border border-[#5BB5B0] rounded-lg px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#008080] transition-colors bg-transparent";
    const labelStyle = "block text-gray-600 font-medium mb-1 text-left";

    return (
        <section className="py-16 bg-[#F4FAFB] scroll-mt-32" id="contact">
            {status === 'success' && (
                <SuccessModal
                    onClose={() => setStatus('')}
                    title="Message Sent!"
                    message="Thank you for contacting us."
                    subMessage="We will get back to you as soon as possible."
                />
            )}

            <div className="container mx-auto px-4 max-w-3xl">
                <div className="text-center mb-6">
                    <h2 className="text-[#D99C3B] font-serif text-5xl md:text-6xl mb-4">Get In Touch</h2>
                    <p className="text-gray-600 text-xl">Join us at Community Plus Care Services</p>
                </div>

                <div className="bg-transparent p-4 md:p-8">
                    <form ref={form} onSubmit={sendEmail} className="space-y-4">

                        {/* Name */}
                        <div>
                            <label className={labelStyle}>Name</label>
                            <input
                                type="text"
                                name="user_name"
                                className={inputStyle}
                                required
                            />
                        </div>

                        {/* Email Id */}
                        <div>
                            <label className={labelStyle}>Email Id</label>
                            <input
                                type="email"
                                name="user_email"
                                className={inputStyle}
                                required
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className={labelStyle}>Phone</label>
                            <input
                                type="tel"
                                name="user_phone"
                                className={inputStyle}
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <label className={labelStyle}>Message</label>
                            <textarea
                                name="message"
                                rows="6"
                                className={inputStyle}
                                required
                            ></textarea>
                        </div>

                        {/* reCAPTCHA */}
                        <div className="flex justify-center pt-4">
                            <ReCAPTCHA
                                key="contact-recaptcha"
                                ref={recaptchaRef}
                                sitekey={RECAPTCHA_SITE_KEY}
                                onChange={handleCaptchaChange}
                                onExpired={handleCaptchaExpired}
                            />
                        </div>

                        {/* Submit Button - Outline Style */}
                        <div className="text-center pt-6">
                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="bg-white border-2 border-[#5BB5B0] text-[#008080] font-bold py-3 px-16 rounded-full hover:bg-teal-50 transition-all duration-300 disabled:opacity-50 uppercase tracking-widest text-lg"
                            >
                                {status === 'sending' ? 'Sending...' : 'SUBMIT'}
                            </button>
                        </div>

                        {status === 'error' && (
                            <div className="text-center text-red-600 font-medium">
                                Failed to send message. Please try again.
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default ContactForm;

